import { Button, Empty, Flex, Row, Tag, Typography } from 'antd';
import he from 'he';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { useQuestionResponses } from '../../../api/Questions';
import DisplayAttributesModal from '../../../components/DisplayAttributesModal';
import SearchableTable from '../../../components/SearchableTable';
import { SegmentType, SurveyUploadType } from '../../../constants';
import useParams from '../../../hooks/useParams';
import { queryType, surveyType } from '../../../types';
import OpenEndedQuestionsSelect from '../OpenEndedQuestionsSelect';
import SegmentResponsesCard from './SegmentResponsesCard';

const { Text } = Typography;

const StyledSearchableTable = styled(SearchableTable)`
  .ant-table-tbody > tr > td {
    border: none;
  }
`;

const StyledResponseText = styled(Text)`
  margin-right: 8px;
`;

function ResponsesTab({
  survey,
  questions,
  questionResponseOptions,
  selectedQuestionId = undefined,
  setSelectedQuestionId,
}) {
  const { surveyId } = useParams();
  const [segments, setSegments] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [attributesModalVisible, setAttributesModalVisible] = useState(false);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState([]);

  const openEndedQuestions = questions.data.filter((q) => q.has_themes);
  const RESPONSE_SEGMENTS_CACHE_KEY = `responseSegments${selectedQuestionId}`;
  const DISPLAY_ATTRIBUTES_CACHE_KEY = `displayAttributes${surveyId}`;

  const filters = useMemo(() => {
    const params = new URLSearchParams(segments.find((s) => s.active)?.segmentParams || '');
    params.set('with_theme_ids', 'true');
    if (selectedAttributeIds.length) {
      params.set('segment_ids', selectedAttributeIds.join(','));
    }
    return params.toString();
  }, [segments, selectedAttributeIds]);

  const { data: responses, isFetching: responsesLoading } = useQuestionResponses(
    {
      questionId: selectedQuestionId,
      filters,
      surveyId,
    },
    {
      enabled: !!selectedQuestionId,
      onSuccess: (data) => {
        if (!segments.length) {
          setSegments([
            {
              id: uuidv4(),
              active: true,
              text: `All Respondents (${data.data.length})`,
              segmentParams: null,
            },
          ]);
        } else if (segments.find((s) => s.active).newlyCreated) {
          // if the segment is new add response count to text and remove newlyCreated
          const updatedSegments = segments.map((s) => {
            if (s.active) {
              const { newlyCreated, ...rest } = s;
              return { ...rest, text: `${s.text} (${data.data.length})` };
            }
            return s;
          });
          setSegments(updatedSegments);
        }
      },
    },
  );

  useEffect(() => {
    const cachedItems = JSON.parse(localStorage.getItem(RESPONSE_SEGMENTS_CACHE_KEY));
    setSegments(cachedItems || []);
  }, [selectedQuestionId, RESPONSE_SEGMENTS_CACHE_KEY]);

  useEffect(() => {
    localStorage.setItem(RESPONSE_SEGMENTS_CACHE_KEY, JSON.stringify(segments));
  }, [segments, RESPONSE_SEGMENTS_CACHE_KEY]);

  useEffect(() => {
    const cached = localStorage.getItem(DISPLAY_ATTRIBUTES_CACHE_KEY);
    setSelectedAttributeIds(cached ? JSON.parse(cached) : []);
  }, [DISPLAY_ATTRIBUTES_CACHE_KEY]);

  useEffect(() => {
    localStorage.setItem(DISPLAY_ATTRIBUTES_CACHE_KEY, JSON.stringify(selectedAttributeIds));
  }, [selectedAttributeIds, DISPLAY_ATTRIBUTES_CACHE_KEY]);

  // whenever the raw data changes (e.g., clicking on a different segment)
  // set page back to 1
  useEffect(() => {
    setPage(1);
  }, [responses]);

  // whenever page changes, scroll back to top
  useEffect(() => {
    window.scroll(0, 0);
  }, [page]);

  const saveSegment = async (selectedChoices) => {
    const segmentText = Object.keys(selectedChoices).flatMap((key) => selectedChoices[key]);
    const params = new URLSearchParams();
    Object.keys(selectedChoices).forEach((key) => {
      if (selectedChoices[key]?.length) {
        params.append(key, selectedChoices[key].join('_$SEPARATOR$_'));
      }
    });
    const newSegment = {
      id: uuidv4(),
      active: true,
      text: `${segmentText.join(', ')}`,
      segmentParams: params.toString(),
      segmentType: SegmentType.CUSTOM,
      newlyCreated: true,
    };
    const updatedSegments = segments.map((s) => ({ ...s, active: false })).concat([newSegment]);
    setSegments(updatedSegments);
  };

  const toggleSegment = (id) => {
    const updatedSegments = segments.map((s) => ({ ...s, active: false }));
    if (segments.find((s) => s.id === id).active) {
      updatedSegments.find((s) => !s.segmentType).active = true;
    } else {
      updatedSegments.find((s) => s.id === id).active = true;
    }
    setSegments(updatedSegments);
  };

  const clearSegment = () => {
    const allRespondentSegment = segments.find((s) => !s.segmentType);
    if (allRespondentSegment.active) {
      allRespondentSegment.text = `All Respondents (${responses.data.length})`;
      setSegments([allRespondentSegment]);
    } else {
      setSegments([]);
    }
  };

  const editSegmentName = (id, value) => {
    const updatedSegments = segments.map((s) => (s.id === id ? { ...s, text: value } : s));
    setSegments(updatedSegments);
  };

  const removeSegment = (id) => {
    const updatedSegments = segments.filter((s) => s.id !== id);
    if (segments.find((s) => s.id === id).active) {
      updatedSegments.find((s) => !s.segmentType).active = true;
    }
    setSegments(updatedSegments);
  };

  const handlePageChange = (newPage, newPageSize) => {
    // if changing page size, return to page 1
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(1);
    } else {
      setPage(newPage);
    }
  };

  const afterSearch = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const columns = [
    {
      dataIndex: 'text',
      render: (text, record) => {
        return (
          <Flex vertical gap={6}>
            <StyledResponseText italic>{`"${he.decode(text)}"`}</StyledResponseText>
            <Row gutter={[8, 8]}>
              {record.segment_responses &&
                Object.entries(record.segment_responses).map(([k, v]) => <Tag key={k}>{v}</Tag>)}
            </Row>
          </Flex>
        );
      },
    },
  ];

  return (
    <Flex vertical gap={16}>
      <OpenEndedQuestionsSelect
        survey={survey}
        openEndedQuestions={openEndedQuestions}
        selectedQuestionId={selectedQuestionId}
        setSelectedQuestionId={setSelectedQuestionId}
      />
      {survey.upload_type === SurveyUploadType.SURVEY && (
        <SegmentResponsesCard
          questions={questions.data.filter((q) => q.is_filterable)}
          questionResponseOptions={questionResponseOptions}
          segments={segments}
          toggleSegment={toggleSegment}
          saveSegment={saveSegment}
          clearSegment={clearSegment}
          editSegmentName={editSegmentName}
          removeSegment={removeSegment}
        />
      )}
      <StyledSearchableTable
        searchPlaceholder="Search responses..."
        showHeader={false}
        pagination={{
          defaultPageSize: 50,
          onChange: handlePageChange,
          current: page,
          pageSize,
          showTotal: (total) => `Total ${total} responses`,
        }}
        baseData={responses?.data}
        columns={columns}
        loading={responsesLoading}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No responses available" />
          ),
        }}
        rowKey="id"
        searchColumns={['text']}
        afterSearch={afterSearch}
        extra={
          survey.upload_type === SurveyUploadType.SURVEY ? (
            <Button type="primary" onClick={() => setAttributesModalVisible(true)}>
              Display attributes
            </Button>
          ) : null
        }
      />
      <DisplayAttributesModal
        visible={attributesModalVisible}
        setVisible={setAttributesModalVisible}
        attributes={questions.data.filter((q) => q.is_filterable)}
        onOk={setSelectedAttributeIds}
        selectedAttributeIds={selectedAttributeIds}
      />
    </Flex>
  );
}

ResponsesTab.propTypes = {
  survey: surveyType.isRequired,
  questions: queryType.isRequired,
  questionResponseOptions: PropTypes.arrayOf(queryType).isRequired,
  selectedQuestionId: PropTypes.number,
  setSelectedQuestionId: PropTypes.func.isRequired,
};

export default ResponsesTab;
